import React, { useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

function DraggablePiece({ text }) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'PIECE',
    item: { text },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }), [text]);

  const renderContent = () => {
    if (text === 'FRACTION') {
      return (
        <div className="flex flex-col items-center text-sm">
          <div>Cash Flow<sub>t</sub></div>
          <div className="border-t border-black w-full my-0.5" style={{ width: '80px' }} />
          <div>(1 + r)<sup>t</sup></div>
        </div>
      );
    }
    return text;
  };

  return (
    <div
      ref={drag}
      className="px-2 py-1 m-1 bg-indigo-100 border rounded cursor-move min-w-[60px] text-center"
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {renderContent()}
    </div>
  );
}

function DropZone({ onDrop, assembled }) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'PIECE',
    drop: (item) => onDrop(item.text),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }), [assembled]);

  return (
    <div
      ref={drop}
      className="min-h-[60px] p-3 border-2 border-dashed border-gray-400 rounded bg-white flex flex-wrap justify-start"
      style={{ backgroundColor: isOver ? '#eef2ff' : 'white' }}
    >
      {assembled.map((text, idx) => (
        <div
          key={idx}
          className="px-2 py-1 m-1 bg-green-100 border rounded text-center"
        >
          {text === 'FRACTION' ? (
            <div className="flex flex-col items-center text-sm">
              <div>Cash Flow<sub>t</sub></div>
              <div className="border-t border-black w-full my-0.5" style={{ width: '80px' }} />
              <div>(1 + r)<sup>t</sup></div>
            </div>
          ) : (
            text
          )}
        </div>
      ))}
    </div>
  );
}

export default function FormulaBuilderDragDrop({ config = {} }) {
  const { prompt = '', pieces = [], answerSequence = [] } = config;
  const [assembled, setAssembled] = useState([]);
  const [result, setResult] = useState(null);

  const handleDrop = (text) => {
    setAssembled((prev) => [...prev, text]);
  };

  const handleReset = () => {
    setAssembled([]);
    setResult(null);
  };

  const handleCheck = () => {
    const isCorrect = JSON.stringify(assembled) === JSON.stringify(answerSequence);
    setResult(isCorrect ? '✅ Correct formula!' : '❌ Try again!');
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="p-4 max-w-3xl mx-auto">
        <h2 className="text-lg font-bold mb-4">{prompt}</h2>

        <DropZone assembled={assembled} onDrop={handleDrop} />

        <div className="my-4 flex flex-wrap gap-2 bg-gray-100 p-3 rounded">
          {pieces.map((piece, idx) => (
            <DraggablePiece key={idx} text={piece} />
          ))}
        </div>

        <div className="flex gap-4 mt-4">
          <button
            onClick={handleCheck}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Check Formula
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Reset
          </button>
        </div>

        {result && (
          <div className="mt-4 text-md font-medium text-center text-indigo-700">{result}</div>
        )}
      </div>
    </DndProvider>
  );
}